import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CORNER_SLOTS,
  FEED_SWITCHES,
  MAX_PARTICIPANT_INFO,
  MAX_PARTICIPANT_TEAM,
  MAX_SOCIAL_LENGTH,
  SOCIALS,
  SEGMENT_MODES,
  SPLIT_FITS,
  SPLIT_LAYOUTS,
  WIDGET_SIZES,
  camOn,
  courseIsFull,
  defaultFeed,
  feedPatch,
  feedVisible,
  geoStatusLabel,
  headshotUrl,
  micOn,
  onAirLight,
  standbyReason,
  type CameraLook,
  type CaptureKind,
  type ClientMessage,
  type OutputState,
  type Participant,
  type ParticipantSocials,
  type FeedSwitch,
  type OnAirLight,
  type PipLayout,
  type SourceFeed,
  type StreamSource,
} from '@rabbitfuel/shared';
import { api } from '../lib/api.ts';
import { useRealtime } from '../lib/realtime.tsx';
import { useStageSurface, useWakeLock } from '../lib/ui.ts';
import {
  peerStateLabel,
  useAudioMonitors,
  useCameraEffects,
  useCapture,
  useDevices,
  useFeedGate,
  usePublisher,
  useSubscriptions,
  useVideoStream,
  type Capture,
  type CameraEffects,
  type DeviceLists,
  type DeviceOption,
} from '../lib/useMedia.ts';
import { canShareScreen, type PeerState } from '../lib/webrtc.ts';
import { useTalkLines, type TalkLine } from '../lib/talk.ts';
import { ChatPanel } from '../components/ChatPanel.tsx';
import { Chevron, ConnPill, StageLegal } from '../components/Chrome.tsx';
import { AppearanceControls } from '../components/AppearanceControls.tsx';
import { LevelMeter } from '../components/LevelMeter.tsx';
import { OnAirLamp } from '../components/OnAirLamp.tsx';
import { RestreamPanel } from '../components/RestreamPanel.tsx';
import { ResultsPanel } from '../components/ResultsPanel.tsx';
import { StreamOverlay, courseChrome, courseFullscreen, isSplit, playerChrome } from '../components/StreamOverlay.tsx';
import { courseOf } from '../lib/course.ts';
import { PlayerBackdrop, backdropChrome } from '../components/PlayerBackdrop.tsx';
import { StandbyCard } from '../components/Standby.tsx';
import { SplitFill } from '../components/SplitFill.tsx';
import { LookDesk } from '../components/LookDesk.tsx';
import { AdDesk, AdDuePrompt, lshapeToggle } from '../components/AdDesk.tsx';
import { IngestDesk } from '../components/IngestDesk.tsx';
import {
  AdFrameLayer,
  AdLabel,
  AdSpot,
  adChrome,
  adFrameChrome,
  sponsorBug,
  useAdBreak,
  useAdFrame,
} from '../components/Ads.tsx';
import { ClipDesk } from '../components/ClipDesk.tsx';
import { lookOf, videoLook } from '../lib/look.ts';
import { TickerDesk } from '../components/TickerDesk.tsx';
import { SocialIcon } from '../components/SocialIcon.tsx';
import { SpotlightButton } from '../components/SpotlightButton.tsx';
import { HEADSHOT_PRESET, fileToDataUrl } from '../lib/images.ts';
import { HelpLink, HelpPanel, HelpProvider, type HelpRequest } from '../components/DirectorHelp.tsx';
import type { HelpTopic } from '../lib/directorHelp.ts';
import { hasSlotKey, keyLabel, readShortcut, type ShortcutHit } from '../lib/shortcuts.ts';
import { loadSession, roleLabel } from '../lib/session.ts';

export function Director() {
  const { slug = '' } = useParams();
  useStageSurface();
  const { state, send } = useRealtime();
  const pin = loadSession(slug)?.organizerPin ?? '';
  const myConnId = state.self?.connectionId;
  // Das Regie-Pult ist der Kontrollraum -- sein Bildschirm geht waehrend des
  // Events nicht aus.
  useWakeLock(true);

  /* ---------------------------------------------- eigene Quelle der Regie */

  // Auf dem Handy sind drei Spalten nebeneinander unbrauchbar -- daher Tabs.
  const [tab, setTab] = useState<'freigaben' | 'programm' | 'chat'>('programm');
  // Am Desktop lassen sich Freigaben (links) und Chat (rechts) einklappen.
  const [columns, setColumns] = useColumns();
  const requestsFoldRef = useRef<HTMLButtonElement | null>(null);
  const requestsRailRef = useRef<HTMLButtonElement | null>(null);
  const chatFoldRef = useRef<HTMLButtonElement | null>(null);
  const chatRailRef = useRef<HTMLButtonElement | null>(null);
  /*
   * Was im eingeklappten Chat neu ist. Gemerkt wird der Zeitpunkt des
   * Einklappens, keine Anzahl -- der Verlauf ist auf 200 Nachrichten gekappt
   * und wuerde ab dann nicht mehr wachsen.
   */
  const [chatFoldedAt, setChatFoldedAt] = useState(() => Date.now());
  const unreadChat = columns.chat ? 0 : state.chat.filter((m) => !m.system && m.ts > chatFoldedAt).length;
  /*
   * Der Fokus wandert mit -- sonst steht er auf einem Knopf, der gerade
   * verschwunden ist. Beim Tastenkuerzel nicht: da stand er nie auf dem Knopf.
   */
  const foldRequests = (open: boolean, moveFocus = true) => {
    setColumns((c) => ({ ...c, requests: open }));
    if (moveFocus) requestAnimationFrame(() => (open ? requestsFoldRef : requestsRailRef).current?.focus());
  };
  const foldChat = (open: boolean, moveFocus = true) => {
    if (!open) setChatFoldedAt(state.chat.at(-1)?.ts ?? Date.now());
    setColumns((c) => ({ ...c, chat: open }));
    if (moveFocus) requestAnimationFrame(() => (open ? chatFoldRef : chatRailRef).current?.focus());
  };

  /* ---------------------------------------------------------------- Hilfe */

  const [help, setHelp] = useState<HelpRequest & { open: boolean }>({ open: false, topic: null, nonce: 0 });
  // Wohin der Fokus nach dem Schliessen zurueckgeht: auf das „?", das sie geoeffnet hat.
  const helpReturn = useRef<HTMLElement | null>(null);
  const openHelp = useCallback((topic?: HelpTopic) => {
    setHelp((h) => {
      if (!h.open) helpReturn.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      return { open: true, topic: topic ?? null, nonce: h.nonce + 1 };
    });
  }, []);
  const closeHelp = useCallback(() => {
    setHelp((h) => ({ ...h, open: false }));
    requestAnimationFrame(() => {
      if (helpReturn.current?.isConnected) helpReturn.current.focus();
      helpReturn.current = null;
    });
  }, []);

  /*
   * Zwei Aufnahmen, nicht eine mit Umschalter: die Regie sendet Bildschirm
   * *und* Kamera gleichzeitig -- Bildschirm als Hauptbild, Kamera im kleinen
   * Fenster. Der Server fuehrt das als zwei Quellen, also halten wir sie auch
   * hier getrennt.
   */
  const cameraCapture = useCapture('camera');
  const screenCapture = useCapture('screen');

  /*
   * Was hinter die Person kommt, wenn die Regie den Hintergrund ersetzt:
   * derselbe Buehnen-Hintergrund, den auch der Player zeigt. Damit passen
   * gesendetes Bild und Rahmen zusammen, statt zwei Motive zu mischen.
   */
  const effectTarget = useMemo(
    () => ({
      url:
        state.stage.backdrop === 'cover'
          ? state.event?.coverUrl ?? null
          : state.stage.backdrop === 'image'
            ? state.event?.backdropUrl ?? null
            : null,
      color: state.event?.accent ?? '#101215',
      blur: state.stage.blur,
      dim: state.stage.dim,
    }),
    [state.stage, state.event],
  );
  // Freistellen gibt es nur fuer die Kamera -- ein Bildschirm hat keinen
  // Hintergrund, der wegkoennte.
  const effects = useCameraEffects(cameraCapture.stream, 'camera', effectTarget);

  /*
   * Gesendet wird das aufbereitete Bild, wenn es eines gibt -- sonst die rohe
   * Kamera. Faellt das Freistellen aus, kostet das also den Effekt und nicht
   * den Stream.
   */
  const cameraOut = effects.stream ?? cameraCapture.stream;

  /* Die eigenen Quellen auf dem Server, je Art eine. */
  const mine = useMemo(
    () => state.sources.filter((s) => s.connectionId === myConnId && s.status !== 'ended'),
    [state.sources, myConnId],
  );
  const myCamera = useMemo(() => mine.find((s) => s.kind === 'camera') ?? null, [mine]);
  const myScreen = useMemo(() => mine.find((s) => s.kind === 'screen') ?? null, [mine]);

  const cameraViewers = usePublisher(cameraOut, myCamera?.id);
  const screenViewers = usePublisher(screenCapture.stream, myScreen?.id);

  /* ------------------------------------------------------ Gegensprechen */

  /*
   * Sprechtasten: Handys, mit denen die Regie gerade spricht, ohne dass sie im
   * Bild sind -- „gleich bist du live". Im Bild ist die Leitung ohnehin offen.
   * Bewusst nur in diesem Tab: ein Vorgespraech ueberlebt keinen Reload, und
   * eine zweite Regie soll nicht in ein fremdes Gespraech geschaltet werden.
   */
  const [talkKeys, setTalkKeys] = useState<ReadonlySet<string>>(() => new Set());
  const onAir = (sourceId: string) => sourceId === state.activeSourceId || sourceId === state.overlaySourceId;
  const toggleTalk = (sourceId: string) =>
    setTalkKeys((prev) => {
      const next = new Set(prev);
      if (!next.delete(sourceId)) next.add(sourceId);
      return next;
    });

  // Ein Vorgespraech endet, sobald das Handy im Bild ist -- dann ist es live --
  // oder sobald es die Quelle nicht mehr gibt.
  useEffect(() => {
    setTalkKeys((prev) => {
      const next = new Set(
        [...prev].filter((id) => {
          const source = state.sources.find((s) => s.id === id);
          return source && source.status === 'approved' && !onAir(id);
        }),
      );
      return next.size === prev.size ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sources, state.activeSourceId, state.overlaySourceId]);

  /*
   * Waehrend eines Vorgespraechs ist das Regie-Mikro im Programm stumm: was
   * die Regie dem naechsten Handy sagt, gehoert nicht in den Stream. Die
   * Sprechleitung bekommt einen eigenen Klon des Mikros und bleibt davon
   * unberuehrt (talk.ts).
   */
  const privateTalk = talkKeys.size > 0;

  /*
   * Werbepause: solange der Spot laeuft, ist das Regie-Mikro im Programm
   * ebenfalls stumm -- und danach von selbst wieder offen. Gemacht wird es
   * wie beim Vorgespraech an den Spuren dieses Geraets, nicht am Schalter auf
   * dem Server: ein Reload mitten in der Pause laesst so kein Mikro
   * versehentlich aus. Die Sprechleitung zu den Handys bleibt offen -- in der
   * Pause bereitet die Regie die naechste Kamera vor.
   */
  const ad = useAdBreak(state.ads, state.adsAt);
  const adOn = Boolean(ad.active);
  const spotRef = useRef<HTMLVideoElement | null>(null);
  // Banner und L-Form laufen stumm -- das Regie-Mikro bleibt dabei offen.
  const frame = useAdFrame(state.ads, state.adsAt, adOn);

  const programFeed = (feed: SourceFeed | undefined) =>
    privateTalk || adOn ? { ...(feed ?? defaultFeed()), mic: false } : feed;

  /*
   * Mikro, Bildschirmton und Bild dieses Geraets folgen dem, was der Server
   * fuehrt -- nicht einem lokalen Zustand. Deshalb steht nach einem Reload
   * wieder dasselbe da, und deshalb ist „stumm" wirklich stumm: die Spur ist
   * aus, bevor sie in eine Verbindung geht.
   */
  useFeedGate(cameraCapture.tracks, programFeed(myCamera?.feed));
  useFeedGate(screenCapture.tracks, programFeed(myScreen?.feed));

  /* An- und Abmelden je Aufnahme. */
  useSourceSlot(cameraCapture, myCamera, myConnId, send, 'Regie · Kamera');
  useSourceSlot(screenCapture, myScreen, myConnId, send, 'Regie · Bildschirm');

  /*
   * Geraeteliste. Neu gelesen, sobald eine Aufnahme laeuft: *Namen* gibt der
   * Browser erst mit Erlaubnis heraus -- vorher heisst alles „Kamera 1".
   */
  const devices = useDevices([Boolean(cameraCapture.stream), Boolean(screenCapture.stream)]);

  /*
   * Ein versehentliches F5 kostet die eigene Kamera fuer ein paar Sekunden --
   * abbrechen tut der Stream deswegen nicht mehr, aber ein Hinweis ist er wert.
   * Der Browser zeigt dabei seinen eigenen Text; der Rueckgabewert genuegt.
   */
  const sending = Boolean(cameraCapture.stream || screenCapture.stream);
  useEffect(() => {
    if (!sending) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [sending]);

  /*
   * „Bildschirm gross, Kamera klein" -- der Griff, um den es beim parallelen
   * Senden ueberhaupt geht. Er laeuft nicht beim Klick, sondern sobald der
   * Server die Bildschirmquelle bestaetigt hat: vorher gibt es keine
   * sourceId, auf die sich ein Hauptbild setzen liesse.
   */
  const wantCombine = useRef(false);
  const combine = useCallback(() => {
    if (!myScreen || myScreen.status !== 'approved') return;
    send({ t: 'program:set', sourceId: myScreen.id });
    if (myCamera?.status === 'approved') send({ t: 'overlay:set', sourceId: myCamera.id });
  }, [myScreen, myCamera, send]);

  useEffect(() => {
    if (!wantCombine.current || !myScreen || myScreen.status !== 'approved') return;
    wantCombine.current = false;
    combine();
  }, [myScreen, combine]);

  /* ------------------------------------------------------ fremde Quellen */

  const pending = useMemo(() => state.sources.filter((s) => s.status === 'pending'), [state.sources]);
  const approved = useMemo(() => state.sources.filter((s) => s.status === 'approved'), [state.sources]);
  /*
   * Feste Nummern fuer die Vorschau und die Tastenkuerzel. Die Kacheln stehen
   * in dieser Reihenfolge -- „3" ist dann auch die dritte Kachel.
   */
  const slots = useSlotNumbers(approved);
  const tiles = useMemo(
    () => [...approved].sort((a, b) => (slots.get(a.id) ?? 0) - (slots.get(b.id) ?? 0)),
    [approved, slots],
  );
  // Die eigene Quelle nicht bei sich selbst abonnieren -- die liegt lokal vor.
  const remote = useMemo(() => approved.filter((s) => s.connectionId !== myConnId), [approved, myConnId]);
  const { streams, states } = useSubscriptions(remote);

  /*
   * Die eigenen Quellen liegen lokal vor -- und zwar zwei: Kamera und
   * Bildschirm. Ohne die Unterscheidung zeigte der Monitor beim Umschneiden
   * zwischen ihnen weiter dasselbe Bild.
   */
  const streamFor = (source: StreamSource) =>
    source.connectionId === myConnId
      ? (source.kind === 'screen' ? screenCapture.stream : cameraOut) ?? undefined
      : streams[source.id];
  const stateFor = (source: StreamSource): PeerState =>
    source.connectionId === myConnId ? 'connected' : (states[source.id] ?? 'new');

  const active = approved.find((s) => s.id === state.activeSourceId) ?? null;
  const overlay = approved.find((s) => s.id === state.overlaySourceId) ?? null;
  const split = isSplit(state.pip, overlay);
  const course = courseOf(state.event?.geofence);
  /*
   * Streckenkarte im Vollbild: der Monitor zeigt sie wie der Stream, mit dem
   * Hauptbild klein in der Ecke und der zweiten Kamera als kleinem Fenster.
   * `split` bleibt, was eingestellt ist -- Knoepfe und Kuerzel richten sich
   * danach --, `splitShown` ist, was gerade im Bild steht.
   */
  const courseFull = !ad.active && courseFullscreen(state.widgets, state.ticker, course);
  const splitShown = split && !courseFull;
  // „Ganz zeigen" im Splitscreen: der Rand jeder Haelfte bekommt eine unscharfe Kopie.
  const fill = splitShown && state.pip.fit === 'contain';
  /*
   * Was der Monitor zeigt, wenn abgeblendet ist -- und warum. Die Regie
   * bekommt dasselbe Bild wie der Stream: blind abzublenden und danach zu
   * raten, ob es wirklich weg ist, waere die schlechtere Haelfte des Features.
   */
  const [speaker, setSpeaker] = useState<string | undefined>(undefined);

  const standby = standbyReason(active?.feed, state.output);
  const programBlanked = Boolean(active) && !feedVisible(active?.feed, state.output);
  const programRef = useVideoStream(active ? streamFor(active) : null);
  const pipRef = useVideoStream(overlay ? streamFor(overlay) : null);

  /*
   * Gegensprechen, Richtung Handy -> Regie: die Regie hoert alle im Bild und
   * alle, mit denen sie per Taste spricht -- nur sich selbst nie. Das eigene
   * Mikro verzoegert zurueckzuhoeren macht das Sprechen unmoeglich und baut
   * ohne Kopfhoerer eine Rueckkopplung. Die Handy-Streams liegen fuer die
   * Vorschau ohnehin vor; abgespielt wird je eines, ueber das gewaehlte Geraet.
   *
   * „Ton aus" am Ausgang gilt hier bewusst nicht: das ist Gegensprechen, kein
   * Abhoeren des Ausgangs -- auch bei stummem Stream will die Regie mit dem
   * Handy reden koennen. Der Mikro-Schalter einer Quelle wirkt dagegen schon
   * beim Sender und damit auch hier.
   */
  const [monitorOn, setMonitorOn] = useState(true);

  /*
   * Den Spot hoert die Regie mit: ueber dasselbe Geraet wie die Handys und
   * unter demselben „Hoeren"-Schalter. „Ton aus" am Ausgang gilt fuer ihn --
   * anders als fuers Gegensprechen -- sehr wohl: der Spot ist Programm, und
   * die Regie soll hoeren, was rausgeht.
   */
  const spotHeard = adOn && monitorOn && !state.output.muted;
  const spotHeardRef = useRef(spotHeard);
  spotHeardRef.current = spotHeard;
  const [spotBlocked, setSpotBlocked] = useState(false);
  useEffect(() => {
    if (!adOn) setSpotBlocked(false);
  }, [adOn]);
  // Hat der Browser den Ton gesperrt (seit dem Laden kein Klick), holt ihn der naechste Klick nach.
  useEffect(() => {
    if (!spotBlocked) return;
    const resume = () => {
      const el = spotRef.current;
      if (!el) return;
      el.muted = !spotHeardRef.current;
      void el.play().catch(() => undefined);
      setSpotBlocked(false);
    };
    window.addEventListener('pointerdown', resume, true);
    window.addEventListener('keydown', resume, true);
    return () => {
      window.removeEventListener('pointerdown', resume, true);
      window.removeEventListener('keydown', resume, true);
    };
  }, [spotBlocked]);
  /*
   * Beim Klick auf „Werbepause" das Spot-Element einmal anstossen: Safari
   * erlaubt Ton spaeter nur einem Element, das schon in einer Beruehrung
   * gestartet wurde -- der Spot selbst kommt erst mit der Antwort des Servers.
   */
  const unlockSpot = useCallback(() => {
    const el = spotRef.current;
    if (!el || adOn) return;
    void el.play().catch(() => undefined);
    el.pause();
  }, [adOn]);

  const heard = approved.filter((s) => s.connectionId !== myConnId && (onAir(s.id) || talkKeys.has(s.id)));
  const monitor = useAudioMonitors(
    heard.map((s) => streams[s.id]).filter((s): s is MediaStream => Boolean(s)),
    { enabled: monitorOn, deviceId: speaker },
  );

  /*
   * Und Regie -> Handy: eine Sprechleitung zu jedem Handy im Gespraech.
   * Waehrend eines Vorgespraechs hoert nur das angesprochene Handy die Regie,
   * nicht das im Bild. Andere Regie-Geraete bekommen keine Leitung -- die
   * sitzen im selben Raum.
   */
  const talkMic = cameraCapture.tracks.mic ?? screenCapture.tracks.mic ?? null;
  /*
   * „Mikro aus" der Regie gilt auch auf den Sprechleitungen. Die Leitungen
   * laufen ueber eigene Klone des Mikros, an denen der Schalter sonst
   * vorbeiginge -- und das Handy hoerte die Regie, die im Stream stumm ist.
   * Massgeblich ist die Quelle, deren Mikro die Leitung traegt.
   */
  const talkFeed = cameraCapture.tracks.mic ? myCamera?.feed : myScreen?.feed;
  const talkMuted = Boolean(talkMic && talkFeed && !micOn(talkFeed));
  const talkLines = useMemo<TalkLine[]>(() => {
    const byConnection = new Map<string, boolean>();
    for (const s of heard) {
      if (s.role === 'organizer') continue;
      const live = !talkMuted && (privateTalk ? talkKeys.has(s.id) : true);
      byConnection.set(s.connectionId, (byConnection.get(s.connectionId) ?? false) || live);
    }
    return [...byConnection].map(([connectionId, live]) => ({ connectionId, live }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heard.map((s) => `${s.id}@${s.connectionId}`).join('|'), privateTalk, talkKeys, talkMuted]);
  useTalkLines(talkMic, talkLines);

  const monitorNote = adOn
    ? [
        sending ? 'Werbepause — dein Mikro ist im Stream bis zum Ende des Spots stumm.' : 'Werbepause läuft.',
        spotBlocked ? 'Klick irgendwo auf die Seite, damit du den Spot hörst.' : '',
      ]
        .filter(Boolean)
        .join(' ')
    : privateTalk
    ? `Vorgespräch — dein Mikro ist im Stream so lange stumm.${talkMuted ? ' Es ist gerade ganz aus: das Handy hört dich nicht.' : ''}`
    : heard.length && monitorOn
      ? `Im Gespräch: ${heard.map((s) => s.label).join(', ')}${
          !talkMic
            ? ' — sie hören dich erst, wenn deine Kamera läuft.'
            : talkMuted
              ? ' — dein Mikro ist aus, sie hören dich nicht.'
              : ''
        }`
      : null;

  /* -------------------------------------------------------- Tastenkuerzel */

  /*
   * „Dein Mikro" ist das, an dem auch die Sprechleitung haengt: das der
   * Kamera, sonst das des Bildschirms. Dasselbe Mikro, das die Handys hoeren.
   */
  const micSource = cameraCapture.tracks.mic ? myCamera : screenCapture.tracks.mic ? myScreen : (myCamera ?? myScreen);
  const bySlot = (n: number) => approved.find((s) => slots.get(s.id) === n) ?? null;

  /*
   * Was ein Kuerzel getan hat, steht kurz oben im Bild -- der Knopf dazu ist
   * oft gerade weggescrollt, und ohne Rueckmeldung drueckte man im Zweifel
   * zweimal und haette es wieder zurueckgeschaltet.
   */
  const [toast, setToast] = useState<{ text: string; id: number } | null>(null);
  const say = (text: string, keys?: string) => setToast({ text: keys ? `${text} · ${keys}` : text, id: Date.now() });
  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const runShortcut = ({ id, slot }: ShortcutHit) => {
    const keys = keyLabel(id, slot);
    const source = slot !== undefined ? bySlot(slot) : null;
    const missing = () => say(`Keine Quelle ${slot}`, keys);
    switch (id) {
      case 'mic': {
        if (!micSource) return say('Kein eigenes Mikro — erst die Kamera starten', keys);
        const on = micSource.feed.mic;
        send({ t: 'source:feed', sourceId: micSource.id, patch: feedPatch('mic', !on) });
        return say(on ? 'Dein Mikro ist aus' : 'Dein Mikro ist an', keys);
      }
      case 'cam': {
        if (!myCamera) return say('Deine Kamera läuft nicht', keys);
        const on = myCamera.feed.cam;
        send({ t: 'source:feed', sourceId: myCamera.id, patch: feedPatch('cam', !on) });
        return say(on ? 'Deine Kamera: Bild aus' : 'Deine Kamera: Bild an', keys);
      }
      case 'mute':
        send({ t: 'output:set', patch: { muted: !state.output.muted } });
        return say(state.output.muted ? 'Ausgang: Ton wieder an' : 'Ausgang stumm — Ton aus', keys);
      case 'blank':
        send({ t: 'output:set', patch: { blanked: !state.output.blanked } });
        return say(state.output.blanked ? 'Abblenden aufgehoben' : 'Abgeblendet — im Stream läuft das Standardbild', keys);
      case 'take':
        if (!source) return missing();
        if (source.id === state.activeSourceId) return say(`${slot} ist schon im Hauptbild`, keys);
        send({ t: 'program:set', sourceId: source.id });
        return say(`Hauptbild: ${slot} · ${source.label}`, keys);
      case 'second': {
        if (!source) return missing();
        const word = state.pip.mode === 'split' ? 'daneben' : 'klein';
        if (source.id === state.activeSourceId) return say(`${slot} ist im Hauptbild`, keys);
        if (source.id === state.overlaySourceId) return say(`${slot} ist schon ${word}`, keys);
        send({ t: 'overlay:set', sourceId: source.id });
        return say(`${word === 'klein' ? 'Klein' : 'Daneben'}: ${slot} · ${source.label}`, keys);
      }
      case 'secondOff':
        if (!overlay) return say('Keine zweite Kamera im Bild', keys);
        send({ t: 'overlay:set', sourceId: null });
        return say(split ? 'Splitscreen aus' : 'Kleines Fenster aus', keys);
      case 'swap':
        if (!split) return say('Tauschen geht nur im Splitscreen', keys);
        send({ t: 'overlay:layout', swap: !state.pip.swap });
        return say('Splitscreen getauscht', keys);
      case 'course': {
        // An und aus im Vollbild. Steht die Karte gerade als Kaestchen, wird
        // sie zum Vollbild -- die gewaehlte Ecke fuer das Hauptbild bleibt.
        if (state.ticker.mode !== 'backyard') return say('Die Streckenkarte gibt es nur beim Backyard', keys);
        if (!course) return say('Das Event hat keine Strecke — Strecken-Zone oder GPX in den Einstellungen', keys);
        const config = state.widgets.course;
        if (config.on && courseIsFull(config)) {
          send({ t: 'widget:set', widget: 'course', on: false });
          return say('Strecke aus — Hauptbild wieder groß', keys);
        }
        send({ t: 'widget:set', widget: 'course', on: true, full: true });
        return say(ad.active ? 'Strecke im Vollbild — sichtbar nach der Werbepause' : 'Strecke im Vollbild', keys);
      }
      case 'lshape': {
        // Dauer und Auswahl wie im Werbe-Block -- dort steht, was gewaehlt ist.
        const msg = lshapeToggle(state.ads);
        if (!msg) return say('Für die L-Form erst bei einem Bild oder Video „L-Form“ anhaken', keys);
        send(msg);
        if (state.ads.frame?.kind === 'lshape') return say('L-Form aus', keys);
        return say(ad.active ? 'L-Form an — sichtbar nach der Werbepause' : 'L-Form an', keys);
      }
      case 'talk': {
        if (!source) return missing();
        // Dieselben Regeln wie die Taste „Sprechen" an der Kachel.
        if (source.connectionId === myConnId || source.role === 'organizer') {
          return say('Mit deinen eigenen Quellen gibt es kein Vorgespräch', keys);
        }
        if (onAir(source.id)) return say(`${slot} ist im Bild — ihr sprecht schon live`, keys);
        const talking = talkKeys.has(source.id);
        if (!talking && !talkMic) return say('Erst deine Kamera starten — das Vorgespräch nutzt ihr Mikro', keys);
        if (!talking && talkMuted) return say('Dein Mikro ist aus — das Handy würde dich nicht hören', keys);
        toggleTalk(source.id);
        return say(talking ? `Vorgespräch mit ${source.label} beendet` : `Vorgespräch mit ${source.label}`, keys);
      }
      case 'monitor':
        setMonitorOn(!monitorOn);
        return say(monitorOn ? 'Hören aus' : 'Hören an', keys);
      // Am Desktop klappen die Spalten, am Handy schalten die Reiter.
      case 'chat':
        if (window.matchMedia('(min-width: 901px)').matches) return foldChat(!columns.chat, false);
        return setTab(tab === 'chat' ? 'programm' : 'chat');
      case 'requests':
        if (window.matchMedia('(min-width: 901px)').matches) return foldRequests(!columns.requests, false);
        return setTab(tab === 'freigaben' ? 'programm' : 'freigaben');
      case 'help':
        return help.open ? closeHelp() : openHelp();
    }
  };
  // Der Lauscher bleibt derselbe, die Entscheidung trifft immer der neueste Stand.
  const runShortcutRef = useRef(runShortcut);
  runShortcutRef.current = runShortcut;
  const helpOpenRef = useRef(help.open);
  helpOpenRef.current = help.open;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && helpOpenRef.current) {
        e.preventDefault();
        closeHelp();
        return;
      }
      const hit = readShortcut(e);
      if (!hit) return;
      e.preventDefault();
      // Gehaltene Taste: ein Schalter, der im Takt der Tastenwiederholung kippt, hilft niemandem.
      if (e.repeat) return;
      runShortcutRef.current(hit);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeHelp]);

  return (
    <HelpProvider value={openHelp}>
      <div className="stage-page">
        <div className="stage-bar">
          <Link to={`/events/${slug}`} className="btn btn--sm btn--onstage">
            ← Event
          </Link>
          <div>
            <div className="stage-bar__title">Regie · {state.event?.title ?? ''}</div>
            <div className="stage-bar__sub">
              {state.presence.total} online · {state.presence.athletes} Athlet:innen ·{' '}
              {approved.filter((s) => s.live).length} Kameras
            </div>
          </div>
          <div className="stage-bar__spacer" />
          {state.instanceId && <span className="stage-bar__sub">Knoten {state.instanceId}</span>}
          <ConnPill status={state.status} />
          <AppearanceControls />
          <button
            type="button"
            className={`btn btn--sm btn--onstage help-open${help.open ? ' is-open' : ''}`}
            aria-pressed={help.open}
            onClick={() => (help.open ? closeHelp() : openHelp())}
            title={`Hilfe und Tastenkürzel (${keyLabel('help')})`}
          >
            <span className="help-open__icon" aria-hidden>
              ?
            </span>
            Hilfe
          </button>
          <Link to={`/events/${slug}/live`} className="btn btn--sm btn--onstage">
            Zuschauersicht
          </Link>
        </div>

        <nav className="director-tabs" role="tablist">
          {(
            [
              ['freigaben', 'Freigaben', pending.length],
              ['programm', 'Programm', approved.length],
              ['chat', 'Chat', state.chat.length],
            ] as const
          ).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={tab === id}
              className={tab === id ? 'is-active' : ''}
              onClick={() => setTab(id)}
            >
              {label}
              {count > 0 && <em>{count}</em>}
            </button>
          ))}
        </nav>

        <div
          className="director-layout"
          data-tab={tab}
          data-left={columns.requests ? 'open' : 'closed'}
          data-right={columns.chat ? 'open' : 'closed'}
        >
          <div className="director-col" data-pane="freigaben">
            <button
              ref={requestsRailRef}
              type="button"
              className="director-rail"
              onClick={() => foldRequests(true)}
              aria-label={`Freigaben und Startliste einblenden${pending.length ? ` (${pending.length} offen)` : ''}`}
              title={`Freigaben und Startliste einblenden (${keyLabel('requests')})`}
            >
              <span className="director-rail__icon">
                <Chevron dir="right" />
              </span>
              {pending.length > 0 && <span className="director-rail__count">{pending.length}</span>}
              <span className="director-rail__label">Freigaben · Startliste</span>
            </button>
            <div className="director-col__head">
              Freigaben
              <HelpLink topic="freigaben" />
              {pending.length > 0 && <span className="badge">{pending.length}</span>}
              <button
                ref={requestsFoldRef}
                type="button"
                className="director-fold"
                onClick={() => foldRequests(false)}
                aria-label="Freigaben und Startliste einklappen"
                title={`Einklappen (${keyLabel('requests')})`}
              >
                <Chevron dir="left" />
              </button>
            </div>
            {pending.map((source) => (
              <div className="request" key={source.id}>
                <div>
                  <b>{source.displayName}</b>
                  <span>
                    {' '}
                    · {roleLabel[source.role]}
                    {source.bib ? ` · Nr. ${source.bib}` : ''}
                  </span>
                </div>
                <span>
                  {source.kind === 'screen' ? 'Bildschirm' : 'Kamera'} · {source.label}
                </span>
                <span className={`geotag geotag--${source.geo.status}`}>
                  {geoStatusLabel[source.geo.status]}
                  {source.geo.status === 'outside' && source.geo.distanceMeters !== null
                    ? ` · ${formatDistance(source.geo.distanceMeters)}`
                    : source.geo.accuracy !== null
                      ? ` · ±${formatDistance(source.geo.accuracy)}`
                      : ''}
                </span>
                <div className="request__actions">
                  <button
                    className="btn btn--sm btn--ok"
                    onClick={() => send({ t: 'stream:decide', sourceId: source.id, decision: 'approve' })}
                  >
                    Freigeben
                  </button>
                  <button
                    className="btn btn--sm btn--onstage"
                    onClick={() => send({ t: 'stream:decide', sourceId: source.id, decision: 'reject' })}
                  >
                    Ablehnen
                  </button>
                </div>
              </div>
            ))}
            {pending.length === 0 && <div className="empty">Keine offenen Anfragen.</div>}

            <RosterPanel
              slug={slug}
              pin={pin}
              open={columns.roster}
              onToggle={() => setColumns((c) => ({ ...c, roster: !c.roster }))}
              spotBib={state.spotlight.active?.bib ?? null}
              send={send}
            />
          </div>

          <div className="director-center" data-pane="programm">
            <CaptureDesk
              camera={cameraCapture}
              screen={screenCapture}
              cameraOut={cameraOut}
              cameraSource={myCamera}
              screenSource={myScreen}
              activeSourceId={state.activeSourceId}
              overlaySourceId={state.overlaySourceId}
              cameraViewers={cameraViewers}
              screenViewers={screenViewers}
              effects={effects}
              devices={devices}
              send={send}
              micSourceId={micSource?.id ?? null}
              onShareScreen={() => {
                wantCombine.current = true;
                void screenCapture.start({ mic: screenCapture.choice.mic });
              }}
              onCombine={combine}
              combinable={Boolean(myScreen && myCamera)}
              monitorOn={monitorOn}
              setMonitorOn={setMonitorOn}
              speaker={speaker}
              setSpeaker={setSpeaker}
              monitorNote={monitorNote}
              monitorBlocked={monitor.blocked}
              onMonitorResume={monitor.resume}
            />

            <div>
              <div className="row gap-8" style={{ marginBottom: 10, flexWrap: 'wrap' }}>
                <span className="tag tag--live">Hauptbild</span>
                <HelpLink topic="hauptbild" />
                <span className="stage-bar__sub">
                  {active ? `${active.label} · ${active.displayName}` : 'nichts geschaltet'}
                  {overlay ? ` ${split ? '+ daneben' : '+ klein'}: ${overlay.label}` : ''}
                </span>
                <div className="row gap-6" style={{ marginLeft: 'auto' }}>
                  <OutputBar output={state.output} send={send} />
                  {overlay && (
                    <button
                      className="btn btn--sm btn--onstage"
                      title={`Zweite Kamera aus dem Bild (${keyLabel('secondOff')})`}
                      onClick={() => send({ t: 'overlay:set', sourceId: null })}
                    >
                      {split ? 'Splitscreen aus' : 'Kleines Fenster aus'}
                    </button>
                  )}
                  {active && (
                    <button className="btn btn--sm btn--onstage" onClick={() => send({ t: 'program:set', sourceId: null })}>
                      Schwarz schalten
                    </button>
                  )}
                </div>
              </div>
              <div
                className="player"
                {...playerChrome({ pip: Boolean(overlay), course: courseFull }, state.widgets, state.pip)}
                {...backdropChrome(state.stage, state.event)}
                {...adChrome(ad, state.ads, Boolean(active && streamFor(active)) && !programBlanked && !state.output.blanked)}
                {...adFrameChrome(frame)}
                {...courseChrome(courseFull, state.widgets.course, {
                  live: Boolean(active && streamFor(active)) && !programBlanked && !state.output.blanked,
                  pipSlot: overlay && !splitShown && !state.output.blanked ? state.pip.slot : null,
                  logoSlot: sponsorBug(state.ads)?.config.slot ?? null,
                })}
              >
                {/* Der Monitor zeigt Hintergrund und Bildkorrektur mit -- sonst
                    stellt die Regie blind ein. */}
                <PlayerBackdrop stage={state.stage} event={state.event} />
                <div className="player__program">
                  {fill && <SplitFill className="split-fill" stream={active ? streamFor(active) : null} blank={programBlanked} />}
                  <video
                    ref={programRef}
                    className={programBlanked ? 'is-blank' : undefined}
                    autoPlay
                    playsInline
                    muted
                    style={videoLook(lookOf(state.cameras, active?.id))}
                  />
                  {/* Der Monitor zeigt das Standardbild genauso wie der Stream --
                      mit einer Zusatzzeile, die nur hier steht: welcher der beiden
                      Knoepfe abgeblendet hat. Sonst sucht die Regie am falschen. */}
                  {standby && (
                    <StandbyCard
                      event={state.event}
                      stage={state.stage}
                      note={
                        standby === 'output'
                          ? 'Ausgang abgeblendet — „Abgeblendet" hebt es auf'
                          : standby === 'own'
                            ? `${active?.label ?? 'Diese Quelle'}: Bild vom Sender selbst aus`
                            : `${active?.label ?? 'Diese Quelle'}: Bild aus`
                      }
                    />
                  )}
                  {/* Mit Ton, ueber das gewaehlte Ausgabegeraet -- siehe spotHeard. */}
                  <AdSpot
                    ad={ad}
                    muted={!spotHeard}
                    videoRef={spotRef}
                    sinkId={speaker}
                    onSoundBlocked={() => setSpotBlocked(true)}
                  />
                  <AdLabel
                    ad={ad}
                    live={Boolean(active && streamFor(active)) && !programBlanked && !state.output.blanked}
                    note={ad.active?.sponsorName}
                  />
                  {overlay && !state.output.blanked && (
                    <div className="pip">
                      {fill && <SplitFill className="pip__fill" stream={streamFor(overlay)} blank={!camOn(overlay.feed)} />}
                      <video
                        ref={pipRef}
                        className={camOn(overlay.feed) ? undefined : 'is-blank'}
                        autoPlay
                        playsInline
                        muted
                        style={videoLook(lookOf(state.cameras, overlay.id))}
                      />
                      <span className="pip__label">{overlay.label}</span>
                    </div>
                  )}
                  {splitShown && active && !state.output.blanked && <span className="split-label">{active.label}</span>}
                  {/* Der Monitor zeigt die Einblendungen mit -- sonst schaltet die
                      Regie blind. */}
                  <StreamOverlay
                    ticker={state.ticker}
                    feed={state.tickerFeed}
                    widgets={state.widgets}
                    sport={state.event?.sport ?? 'sonstiges'}
                    sponsor={sponsorBug(state.ads)}
                    weather={state.weather}
                    course={course}
                    results={state.results}
                    spotlight={state.spotlight}
                  />
                  {active?.reconnecting && (
                    <span className="preview__tag preview__tag--warn player__reconnect">
                      {active.displayName} verbindet neu — Bild steht
                    </span>
                  )}
                  {(!active || !streamFor(active)) && !standby && (
                    <div className="player__idle">
                      <b>{active ? peerStateLabel[stateFor(active)] : 'Kein Bild im Hauptstream'}</b>
                      <span>Wähle unten eine freigegebene Kamera aus.</span>
                    </div>
                  )}
                </div>
                <AdFrameLayer view={frame} ads={state.ads} />
              </div>

              {/* Kleines Fenster oder Splitscreen -- nur relevant, solange
                  ueberhaupt eine zweite Kamera daliegt. */}
              {overlay && <PipLayoutBar pip={state.pip} send={send} />}
            </div>

            <PreviewDeck count={approved.length}>
              <div className="preview-grid">
                {tiles.map((source) => (
                  <PreviewTile
                    key={source.id}
                    source={source}
                    slot={slots.get(source.id)}
                    stream={streamFor(source)}
                    look={lookOf(state.cameras, source.id)}
                    peerState={stateFor(source)}
                    isSelf={source.connectionId === myConnId}
                    onAir={source.id === state.activeSourceId}
                    isOverlay={source.id === state.overlaySourceId}
                    split={state.pip.mode === 'split'}
                    light={onAirLight(source, state.activeSourceId, state.overlaySourceId)}
                    talk={
                      source.connectionId === myConnId || source.role === 'organizer'
                        ? null
                        : onAir(source.id)
                          ? 'live'
                          : talkKeys.has(source.id)
                            ? 'key'
                            : 'off'
                    }
                    talkBlock={!talkMic ? 'nomic' : talkMuted ? 'muted' : null}
                    onTalk={() => toggleTalk(source.id)}
                    send={send}
                    onTake={() => send({ t: 'program:set', sourceId: source.id })}
                    onPip={() => send({ t: 'overlay:set', sourceId: source.id })}
                    onCue={() => send({ t: 'source:cue', sourceId: source.id, on: !source.cued })}
                    onDrop={() => send({ t: 'stream:drop', sourceId: source.id })}
                  />
                ))}
              </div>
              {approved.length === 0 && (
                <div className="empty">
                  Noch keine freigegebene Quelle. Starte oben deine eigene Kamera oder warte auf eine Anfrage.
                </div>
              )}
            </PreviewDeck>

            {/* Botschaften gleich unter den Kameras: beides ist, was von draussen hereinkommt. */}
            {state.clips && <ClipDesk clips={state.clips} ad={ad} send={send} onBeforeAir={unlockSpot} />}

            <TickerDesk
              slug={slug}
              sport={state.event?.sport ?? 'sonstiges'}
              finished={state.event?.status === 'finished'}
              ticker={state.ticker}
              feed={state.tickerFeed}
              widgets={state.widgets}
              weather={state.weather}
              course={course}
              results={state.results}
              spotlight={state.spotlight}
              send={send}
            />

            <LookDesk
              stage={state.stage}
              cameras={state.cameras}
              sources={approved}
              activeSourceId={state.activeSourceId}
              event={state.event}
              send={send}
            />

            <AdDesk
              slug={slug}
              pin={pin || undefined}
              ads={state.ads}
              receivedAt={state.adsAt}
              ad={ad}
              output={state.output}
              send={send}
              onBeforeBreak={unlockSpot}
            />

            {/* Woher die Zieleinlaeufe kommen -- von Hand oder aus RACE RESULT.
                Steht direkt unter dem Ticker-Pult, weil es genau das befuellt. */}
            <ResultsPanel slug={slug} pin={pin || undefined} status={state.results} />

            {/* Eingang und Ausgang nebeneinander: Drohne rein, Stream raus. */}
            <IngestDesk slug={slug} pin={pin || undefined} sources={state.sources} />

            <RestreamPanel slug={slug} pin={pin || undefined} status={state.restream} />

            <StageLegal />
          </div>

          <div className="director-col director-col--chat" data-pane="chat">
            <button
              ref={chatRailRef}
              type="button"
              className="director-rail"
              onClick={() => foldChat(true)}
              aria-label={`Chat einblenden${unreadChat ? ` (${unreadChat} neu)` : ''}`}
              title={`Chat einblenden (${keyLabel('chat')})`}
            >
              <span className="director-rail__icon">
                <Chevron dir="left" />
              </span>
              {unreadChat > 0 && <span className="director-rail__count">{unreadChat}</span>}
              <span className="director-rail__label">Chat</span>
            </button>
            <ChatPanel
              messages={state.chat}
              presence={state.presence}
              disabled={state.status !== 'open'}
              onSend={(text) => send({ t: 'chat:send', text })}
              headAction={
                <button
                  ref={chatFoldRef}
                  type="button"
                  className="director-fold"
                  onClick={() => foldChat(false)}
                  aria-label="Chat einklappen"
                  title={`Einklappen (${keyLabel('chat')})`}
                >
                  <Chevron dir="right" />
                </button>
              }
            />
          </div>
        </div>

        <AdDuePrompt
          ads={state.ads}
          receivedAt={state.adsAt}
          running={adOn}
          send={send}
          onBeforeBreak={unlockSpot}
        />

        {help.open && <HelpPanel request={help} onClose={closeHelp} />}

        <div className="shortcut-toast-wrap" role="status" aria-live="polite">
          {toast && (
            <div className="shortcut-toast" key={toast.id}>
              {toast.text}
            </div>
          )}
        </div>
      </div>
    </HelpProvider>
  );
}

/* ------------------------------------------------ Spalten einklappen */

const COLUMNS_KEY = 'rabbitfuel:regie-spalten';

/** Was in der Regie offen ist -- `true` heisst offen. */
interface Columns {
  requests: boolean;
  chat: boolean;
  /** Die Startliste innerhalb der linken Spalte. */
  roster: boolean;
}

/**
 * Offen oder eingeklappt merkt sich der Browser, ueber Events hinweg: wer den
 * Chat beim Schneiden nicht braucht, will ihn nicht bei jedem Event neu
 * wegklappen.
 */
function useColumns() {
  const [columns, setColumns] = useState<Columns>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLUMNS_KEY) ?? 'null') as Partial<Columns> | null;
      return { requests: saved?.requests !== false, chat: saved?.chat !== false, roster: saved?.roster !== false };
    } catch {
      return { requests: true, chat: true, roster: true };
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLUMNS_KEY, JSON.stringify(columns));
    } catch {
      /* Privater Modus -- dann eben nur bis zum Neuladen. */
    }
  }, [columns]);
  return [columns, setColumns] as const;
}

/* --------------------------------------------------- Nummern der Quellen */

/**
 * Jede freigegebene Quelle bekommt eine Nummer, die bleibt, solange sie
 * verbunden ist. Geht Quelle 2, rutscht die 3 nicht auf die 2 -- sonst
 * schnitte „Strg+Umschalt+2" ploetzlich auf eine andere Kamera als eben
 * noch. Eine neue Quelle nimmt die kleinste freie Nummer, damit die
 * Kuerzel 1 bis 9 nicht nach ein paar Wiederverbindungen ausgehen.
 */
function useSlotNumbers(sources: StreamSource[]): ReadonlyMap<string, number> {
  const kept = useRef<Map<string, number>>(new Map());
  const key = sources.map((s) => s.id).join('|');
  return useMemo(() => {
    const next = new Map<string, number>();
    for (const s of sources) {
      const n = kept.current.get(s.id);
      if (n !== undefined) next.set(s.id, n);
    }
    const used = new Set(next.values());
    let free = 1;
    for (const s of sources) {
      if (next.has(s.id)) continue;
      while (used.has(free)) free++;
      next.set(s.id, free);
      used.add(free);
    }
    kept.current = next;
    return next;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

/* ------------------------------------------------ Quelle an- und abmelden */

/**
 * Meldet eine laufende Aufnahme beim Server als Quelle an -- und wieder ab.
 *
 * Der Zustand, der hier verwaltet wird, ist genau einer: „was haben wir dem
 * Server zuletzt gesagt". Ohne ihn wuerde jeder Renderdurchlauf erneut
 * anmelden. Und er wird bei jeder neuen Verbindung *aus dem Serverzustand*
 * uebernommen, nicht aus unserem Wunsch -- sonst schickt die Regie nach einem
 * Reload ein `stream:cancel` fuer die Quelle, die der Server ihr gerade
 * zurueckgegeben hat, und raeumt damit das Hauptbild bei allen Zuschauern ab.
 */
function useSourceSlot(
  capture: Capture,
  source: StreamSource | null,
  connectionId: string | undefined,
  send: (m: ClientMessage) => void,
  label: string,
) {
  const announced = useRef<boolean | null>(null);

  // Neue Verbindung: erst wieder vom Server lernen, was laeuft.
  useEffect(() => {
    announced.current = null;
  }, [connectionId]);

  useEffect(() => {
    if (!connectionId) return;
    if (announced.current === null) announced.current = source !== null;
    /*
     * Solange die Wiederaufnahme laeuft oder als Angebot ansteht, ist „kein
     * Stream" *kein* Abschalten -- in diesen Sekunden waere das Abmelden ein
     * Abbruch, den die Uebernahme gerade verhindern soll.
     */
    if (capture.resuming || capture.resumable) return;
    const running = capture.stream !== null;
    if (running === announced.current) return;
    announced.current = running;
    if (running) send({ t: 'stream:request', kind: capture.kind, label });
    else if (source) send({ t: 'stream:cancel', sourceId: source.id });
  }, [connectionId, capture.stream, capture.resuming, capture.resumable, capture.kind, source, send, label]);

  // Freigegeben und Medien laufen: der Regie melden, dass Bild da ist.
  useEffect(() => {
    if (source?.status === 'approved' && capture.stream && !source.live) {
      send({ t: 'stream:live', live: true, sourceId: source.id });
    }
  }, [source, capture.stream, send]);
}

/* ------------------------------------------- zweite Kamera anordnen */

/**
 * Wie die zweite Kamera im Bild steht: klein in einer Ecke oder im
 * Splitscreen neben dem Hauptbild. Beim kleinen Fenster gelten Ecke und
 * Groesse, im Splitscreen Aufteilung, Seiten und Ausschnitt.
 */
function PipLayoutBar({ pip, send }: { pip: PipLayout; send: (m: ClientMessage) => void }) {
  const layout = (patch: Omit<Extract<ClientMessage, { t: 'overlay:layout' }>, 't'>) =>
    send({ t: 'overlay:layout', ...patch });
  return (
    <div className="piplayout">
      <div className="piplayout__size piplayout__size--text" role="group" aria-label="Zweite Kamera">
        <button type="button" className={pip.mode === 'pip' ? 'is-on' : ''} onClick={() => layout({ mode: 'pip' })}>
          Kleines Fenster
        </button>
        <button
          type="button"
          className={pip.mode === 'split' ? 'is-on' : ''}
          title="Beide Kameras gleichberechtigt im Bild"
          onClick={() => layout({ mode: 'split' })}
        >
          Splitscreen
        </button>
      </div>
      <HelpLink topic="zweite-kamera" />
      {pip.mode === 'pip' ? (
        <>
          <div className="chip-row">
            {CORNER_SLOTS.map((slot) => (
              <button
                key={slot.id}
                type="button"
                className={`chip${pip.slot === slot.id ? ' is-active' : ''}`}
                onClick={() => layout({ slot: slot.id })}
              >
                {slot.label}
              </button>
            ))}
          </div>
          <div className="piplayout__size" role="group" aria-label="Größe kleines Fenster">
            {WIDGET_SIZES.map((size) => (
              <button
                key={size.id}
                type="button"
                className={pip.size === size.id ? 'is-on' : ''}
                title={`Kleines Fenster: ${size.label}`}
                onClick={() => layout({ size: size.id })}
              >
                {size.short}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="chip-row">
            {SPLIT_LAYOUTS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`chip${pip.split === option.id ? ' is-active' : ''}`}
                title={option.hint}
                onClick={() => layout({ split: option.id })}
              >
                {option.label}
              </button>
            ))}
            <button
              type="button"
              className={`chip${pip.swap ? ' is-active' : ''}`}
              title={`${pip.split === 'stack' ? 'Oben und unten tauschen' : 'Links und rechts tauschen'} (${keyLabel('swap')})`}
              onClick={() => layout({ swap: !pip.swap })}
            >
              ⇄ Tauschen
            </button>
          </div>
          <div className="piplayout__size piplayout__size--text" role="group" aria-label="Ausschnitt im Splitscreen">
            {SPLIT_FITS.map((fit) => (
              <button
                key={fit.id}
                type="button"
                className={pip.fit === fit.id ? 'is-on' : ''}
                title={fit.hint}
                onClick={() => layout({ fit: fit.id })}
              >
                {fit.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------ eigene Aufnahme der Regie */

/**
 * Das Pult fuer die eigenen Aufnahmen: Kamera und geteilter Bildschirm,
 * gleichzeitig.
 *
 * Beide stehen untereinander statt hinter einem Umschalter, weil sie
 * gleichzeitig laufen duerfen -- genau darum geht es: Bildschirm ins
 * Hauptbild, Kamera ins kleine Fenster. Der Knopf „Bildschirm gross, Kamera
 * klein" macht daraus einen Handgriff; Ecke und Groesse der Kamera stellt
 * danach die Leiste unter dem Monitor.
 */
function CaptureDesk({
  camera,
  screen,
  cameraOut,
  cameraSource,
  screenSource,
  activeSourceId,
  overlaySourceId,
  cameraViewers,
  screenViewers,
  effects,
  devices,
  send,
  onShareScreen,
  onCombine,
  combinable,
  monitorOn,
  setMonitorOn,
  speaker,
  setSpeaker,
  monitorNote,
  monitorBlocked,
  onMonitorResume,
  micSourceId,
}: {
  camera: Capture;
  screen: Capture;
  /** Was von der Kamera wirklich rausgeht -- mit Freistellen, wenn aktiv. */
  cameraOut: MediaStream | null;
  cameraSource: StreamSource | null;
  screenSource: StreamSource | null;
  activeSourceId: string | null;
  overlaySourceId: string | null;
  cameraViewers: number;
  screenViewers: number;
  effects: CameraEffects;
  devices: DeviceLists;
  send: (m: ClientMessage) => void;
  onShareScreen: () => void;
  onCombine: () => void;
  combinable: boolean;
  monitorOn: boolean;
  setMonitorOn: (on: boolean) => void;
  speaker: string | undefined;
  setSpeaker: (id: string | undefined) => void;
  monitorNote: string | null;
  monitorBlocked: boolean;
  onMonitorResume: () => void;
  /** Die Quelle, deren Mikro „Strg+M" schaltet -- nur dort steht das Kuerzel im Tooltip. */
  micSourceId: string | null;
}) {
  const micKey = (source: StreamSource | null) =>
    source && source.id === micSourceId ? { mic: keyLabel('mic') } : {};
  return (
    <div className="desk-capture">
      <CaptureRow
        capture={camera}
        source={cameraSource}
        light={onAirLight(cameraSource, activeSourceId, overlaySourceId)}
        small={cameraSource?.id === overlaySourceId}
        preview={cameraOut}
        viewers={cameraViewers}
        title="Kamera"
        idle="Kamera der Regie als Quelle einspeisen."
        startLabel="Kamera starten"
        onStart={() => void camera.start({ camera: camera.choice.camera, mic: camera.choice.mic })}
        send={send}
        keys={{ ...micKey(cameraSource), cam: keyLabel('cam') }}
      >
        <div className="desk__pickers">
          <DevicePicker
            label="Kamera"
            options={devices.cameras}
            value={camera.choice.camera}
            disabled={camera.busy}
            /* Im Betrieb umschalten heisst: neu oeffnen, ohne abzumelden --
               die Quelle bleibt, die Verbindungen ziehen per replaceTrack um. */
            onChange={(id) => void camera.start({ ...camera.choice, camera: id })}
          />
          <DevicePicker
            label="Mikrofon"
            options={devices.mics}
            value={camera.choice.mic}
            disabled={camera.busy}
            onChange={(id) => void camera.start({ ...camera.choice, mic: id })}
          />
        </div>
        {camera.stream && effects.available && <SegmentBar effects={effects} />}
      </CaptureRow>

      <CaptureRow
        capture={screen}
        source={screenSource}
        light={onAirLight(screenSource, activeSourceId, overlaySourceId)}
        small={screenSource?.id === overlaySourceId}
        preview={screen.stream}
        viewers={screenViewers}
        title="Bildschirm"
        idle="Fenster, Tab oder ganzen Bildschirm teilen — mit Mikrofon."
        startLabel="Bildschirm teilen"
        restartLabel="Anderes Fenster"
        onStart={onShareScreen}
        send={send}
        keys={micKey(screenSource)}
        unavailable={
          canShareScreen() ? undefined : (
            <>
              Dieser Browser kann den Bildschirm nicht teilen. Auf dem iPhone geht das im Browser erst ab iOS 27 —
              in Safari und Chrome gleichermaßen. Bis dahin: den Bildschirm mit einer App wie <b>Larix Screencaster</b>{' '}
              an einen RTMP-Eingang senden (Block „RTMP-Eingang“ weiter unten).
            </>
          )
        }
      >
        <div className="desk__pickers">
          <DevicePicker
            label="Mikrofon"
            options={devices.mics}
            value={screen.choice.mic}
            disabled={screen.busy || !screen.stream}
            hint="Geht zusammen mit dem Ton des geteilten Fensters raus."
            onChange={(id) => void screen.start({ ...screen.choice, mic: id })}
          />
        </div>
        {screen.stream && screen.micError && <div className="notice notice--stage">{screen.micError}</div>}
        {combinable && (
          <button className="btn btn--sm btn--onstage" style={{ alignSelf: 'flex-start' }} onClick={onCombine}>
            Bildschirm groß, Kamera klein
          </button>
        )}
      </CaptureRow>

      {/* Was die Regie selbst hoert: die Handys im Gespraech, nie sich selbst. */}
      <div className="desk__monitor">
        <button
          className={`btn btn--sm${monitorOn ? '' : ' btn--onstage'}`}
          aria-pressed={monitorOn}
          onClick={() => setMonitorOn(!monitorOn)}
          title={`Handys im Bild und im Gespräch hören — dein eigenes Mikro hörst du nicht (${keyLabel('monitor')})`}
        >
          {monitorOn ? '🎧 Hören an' : '🎧 Hören aus'}
        </button>
        <HelpLink topic="hoeren" />
        {monitorBlocked && (
          <button className="btn btn--sm" onClick={onMonitorResume} title="Der Browser wartet auf einen Klick">
            🔈 Ton freigeben
          </button>
        )}
        <DevicePicker
          label="Ausgabe"
          options={devices.speakers}
          value={speaker}
          disabled={!devices.canPickSpeaker}
          hint={devices.canPickSpeaker ? undefined : 'Dieser Browser lässt die Wahl beim Betriebssystem.'}
          onChange={setSpeaker}
        />
        {monitorNote && <span className="stage-bar__sub">{monitorNote}</span>}
      </div>
    </div>
  );
}

/** Eine Aufnahme: Vorschau, Schalter, Auswahlmenues. */
function CaptureRow({
  capture,
  source,
  light,
  small,
  preview,
  viewers,
  title,
  idle,
  startLabel,
  /** Beschriftung im Betrieb. Ohne sie gibt es dann keinen Knopf. */
  restartLabel,
  onStart,
  send,
  keys,
  unavailable,
  children,
}: {
  capture: Capture;
  source: StreamSource | null;
  /** Die Ampel dieser Aufnahme -- dieselbe, die ein Handy auf /senden sieht. */
  light: OnAirLight;
  /** Im kleinen Fenster statt im Hauptbild. */
  small: boolean;
  preview: MediaStream | null;
  viewers: number;
  title: string;
  idle: string;
  startLabel: string;
  restartLabel?: string;
  onStart: () => void;
  send: (m: ClientMessage) => void;
  /** Tastenkuerzel der Schalter, fuer die Tooltips. */
  keys?: Partial<Record<FeedSwitch, string>>;
  /** Geht auf diesem Geraet nicht -- dann steht statt des Knopfs, warum und was stattdessen. */
  unavailable?: ReactNode;
  children?: ReactNode;
}) {
  const previewRef = useVideoStream(preview);
  const live = source?.live ?? false;
  return (
    <div className="capture">
      <div className="capture__preview">
        <video ref={previewRef} autoPlay playsInline muted />
        {!capture.stream && <div className="placeholder">aus</div>}
        {capture.stream &&
          (live ? (
            <OnAirLamp light={light} text={lampText(light, small)} size="sm" className="ampel--tag" />
          ) : (
            <span className="preview__tag">startet</span>
          ))}
      </div>
      <div className="capture__body">
        <div>
          <div className="row gap-6">
            <b style={{ fontSize: 14 }}>{title}</b>
            <HelpLink topic="kamera" />
          </div>
          <p className="stage-bar__sub" style={{ marginTop: 2 }}>
            {capture.stream ? `läuft · ${viewers} ${viewers === 1 ? 'Empfänger' : 'Empfänger'}` : idle}
          </p>
        </div>

        {capture.error && <div className="notice notice--stage">{capture.error}</div>}
        {unavailable && !capture.stream && <div className="notice notice--stage">{unavailable}</div>}

        {/*
          * Eine Bildschirmfreigabe laeuft nach einem Reload nicht von selbst
          * wieder an -- `getDisplayMedia` verlangt in jedem Browser einen
          * frischen Klick. Deshalb hier ein Angebot statt stillem Nichts.
          */}
        {capture.resumable && !capture.stream && (
          <div className="notice notice--stage notice--resume">
            <span>Der Bildschirm war vor dem Neuladen freigegeben. Browser verlangen dafür einen Klick.</span>
            <div className="row gap-8">
              <button className="btn btn--sm" disabled={capture.busy} onClick={onStart}>
                Wieder teilen
              </button>
              <button className="btn btn--sm btn--onstage" onClick={capture.dismissResume}>
                Nicht mehr
              </button>
            </div>
          </div>
        )}

        <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
          {/* Laeuft die Kamera, waehlt man das Geraet unten im Menue -- ein
              zweiter Startknopf waere derselbe Weg mit Browserdialog. */}
          {(!capture.stream || restartLabel) && !(unavailable && !capture.stream) && (
            <button
              className={`btn btn--sm${capture.stream ? ' btn--onstage' : ''}`}
              disabled={capture.busy}
              onClick={onStart}
            >
              {capture.stream ? restartLabel : startLabel}
            </button>
          )}
          {capture.stream && (
            <button className="btn btn--sm btn--onstage" onClick={capture.stop}>
              Beenden
            </button>
          )}
          {/*
            * Mikro, Bild und -- beim Bildschirm -- der Bildschirmton. Stehen
            * bei der Aufnahme und nicht unten im Pult, weil es die Knoepfe
            * sind, die im Zweifel in einer Sekunde gefunden werden muessen.
            */}
          {capture.stream && source && <FeedSwitches source={source} send={send} keys={keys} />}
        </div>

        {!(unavailable && !capture.stream) && children}
      </div>
    </div>
  );
}

/**
 * Ein Auswahlmenue fuer ein Geraet.
 *
 * „Automatisch" ist bewusst die erste Wahl und der Standard: am Wettkampftag
 * steckt jemand ein Headset ein, und der Browser trifft dann die richtige
 * Entscheidung von selbst.
 */
function DevicePicker({
  label,
  options,
  value,
  disabled,
  hint,
  onChange,
}: {
  label: string;
  options: DeviceOption[];
  value: string | undefined;
  disabled?: boolean;
  hint?: string;
  onChange: (id: string | undefined) => void;
}) {
  return (
    <label className="picker" title={hint}>
      <span>{label}</span>
      <select
        value={value ?? ''}
        disabled={disabled || options.length === 0}
        onChange={(e) => onChange(e.target.value || undefined)}
      >
        <option value="">{options.length ? 'Automatisch' : 'keine gefunden'}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ------------------------------------------------------- Ton und Bild */

/**
 * Die Schalter einer Quelle: Mikro, Kamera, Bildschirmton.
 *
 * Geschickt wird nur die Absicht -- abgeschaltet wird auf dem Geraet, das
 * sendet. Das gilt auch fuer ein fremdes Handy: es hoert seinen eigenen
 * Zustand aus der Quellenliste und legt seine Spuren selbst still. Deshalb
 * ist „stumm" hier nicht „wir lassen es weg", sondern „es kommt nichts mehr".
 *
 * Der Bildschirmton taucht nur bei einer Bildschirmuebertragung auf; eine
 * Kamera hat keinen.
 */
function FeedSwitches({
  source,
  send,
  compact = false,
  keys,
}: {
  source: StreamSource;
  send: (m: ClientMessage) => void;
  compact?: boolean;
  keys?: Partial<Record<FeedSwitch, string>>;
}) {
  const switches = FEED_SWITCHES.filter((sw) => sw.id !== 'system' || source.kind === 'screen');
  /* Ein geteiltes Fenster hat kein „Kamerabild" -- es hat ein Bild. */
  const labelOf = (id: FeedSwitch, label: string) => (id === 'cam' && source.kind === 'screen' ? 'Bild' : label);
  /*
   * Was der Sender selbst ausgeschaltet hat, steht daneben -- als Schild, nicht
   * als Knopf: zurueckholen kann es nur er. Ohne das Schild saehe die Regie
   * einen gruenen Mikro-Knopf und haette trotzdem Stille.
   */
  const held = [!source.feed.own.mic && 'Mikro', !source.feed.own.cam && 'Bild'].filter(Boolean).join(' + ');
  return (
    <div className={`feed${compact ? ' feed--compact' : ''}`}>
      {switches.map((sw) => {
        const on = source.feed[sw.id];
        return (
          <button
            key={sw.id}
            type="button"
            className={`feed__btn${on ? '' : ' is-off'}`}
            title={keys?.[sw.id] ? `${sw.hint} (${keys[sw.id]})` : sw.hint}
            aria-pressed={!on}
            onClick={() => send({ t: 'source:feed', sourceId: source.id, patch: feedPatch(sw.id, !on) })}
          >
            <span aria-hidden>{sw.icon}</span>
            <span className={compact ? 'only-wide' : undefined}>
              {on ? labelOf(sw.id, sw.label) : `${labelOf(sw.id, sw.label)} aus`}
            </span>
          </button>
        );
      })}
      {held && (
        <span className="feed__own" title="Am Handy selbst ausgeschaltet — nur die Person dort kann es wieder einschalten.">
          <span aria-hidden>✋</span>
          <span>
            <span className={compact ? 'only-wide' : undefined}>Sender: </span>
            {held} aus
          </span>
        </span>
      )}
    </div>
  );
}

/**
 * Die beiden Master-Schalter des Ausgangs.
 *
 * Der Unterschied zu den Schaltern einer Quelle: die hier gelten fuer das
 * Programm und damit auch fuer die naechste Kamera, auf die umgeschnitten
 * wird. Wenn im Hintergrund etwas gesagt wird, das nicht raus soll, ist das
 * der Knopf -- ohne erst zu suchen, welches Handy gerade on air ist.
 */
function OutputBar({ output, send }: { output: OutputState; send: (m: ClientMessage) => void }) {
  return (
    <>
      <button
        className={`btn btn--sm${output.muted ? '' : ' btn--onstage'}`}
        title={`Ton des ganzen Ausgangs — gilt für jede Quelle im Hauptbild (${keyLabel('mute')})`}
        aria-pressed={output.muted}
        onClick={() => send({ t: 'output:set', patch: { muted: !output.muted } })}
      >
        {output.muted ? '🔇 Ton ist aus' : 'Ton aus'}
      </button>
      <button
        className={`btn btn--sm${output.blanked ? '' : ' btn--onstage'}`}
        title={`Bild abblenden — im Stream läuft dann das Standardbild (${keyLabel('blank')})`}
        aria-pressed={output.blanked}
        onClick={() => send({ t: 'output:set', patch: { blanked: !output.blanked } })}
      >
        {output.blanked ? 'Abgeblendet' : 'Abblenden'}
      </button>
    </>
  );
}

function SegmentBar({ effects }: { effects: CameraEffects }) {
  return (
    <div className="stack gap-8">
      <div className="row gap-8" style={{ flexWrap: 'wrap' }}>
        <span className="stage-bar__sub">Hintergrund</span>
        <div className="chip-row">
          {SEGMENT_MODES.map((mode) => (
            <button
              key={mode.id}
              type="button"
              className={`chip${effects.mode === mode.id ? ' is-active' : ''}`}
              title={mode.hint}
              disabled={effects.busy}
              onClick={() => effects.setMode(mode.id)}
            >
              {mode.label}
            </button>
          ))}
        </div>
        {effects.busy && <span className="stage-bar__sub">Modell wird geladen …</span>}
        {/* Die Bildrate steht dabei, weil Freistellen Rechenzeit kostet --
            wer unter 15 fps landet, soll es sehen und nicht raten. */}
        {!effects.busy && effects.stats && effects.mode !== 'off' && (
          <span className="stage-bar__sub">
            {effects.stats.fps} fps · {effects.stats.gpu ? 'Grafikkarte' : 'CPU'}
          </span>
        )}
      </div>
      {effects.error && <div className="notice notice--stage">{effects.error}</div>}
    </div>
  );
}

const formatDistance = (m: number) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${m} m`);

/* ------------------------------------------------------------- Vorschau */

const PREVIEW_OPEN_KEY = 'rabbitfuel:regie-vorschau';

/**
 * Die freigegebenen Quellen als eigener Block zum Aufklappen -- wie Ticker
 * und Werbung. Er steht direkt unter dem Monitor, weil die Regie von hier
 * aus schneidet. Offen ist er, bis jemand ihn zuklappt; das merkt sich der
 * Browser.
 */
function PreviewDeck({ count, children }: { count: number; children: ReactNode }) {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(PREVIEW_OPEN_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const toggle = () => {
    const next = !open;
    setOpen(next);
    try {
      localStorage.setItem(PREVIEW_OPEN_KEY, next ? '1' : '0');
    } catch {
      /* Privater Modus -- dann eben nur bis zum Neuladen. */
    }
  };

  return (
    <section className={`deck deck--preview${open ? '' : ' is-collapsed'}`}>
      <header className="deck__head">
        <button type="button" className="deck__toggle" onClick={toggle} aria-expanded={open}>
          {open ? '▾' : '▸'} Vorschau<span className="deck__toggle-more"> · freigegebene Quellen</span>
        </button>
        <HelpLink topic="vorschau" />
        <span className={`deck__mode${count ? '' : ' deck__mode--off'}`}>{count} verbunden</span>
      </header>
      {open && <div className="deck__block">{children}</div>}
    </section>
  );
}

/** Schild der Ampel auf einer Vorschau -- kurz, es steht im Bild. */
const lampText = (light: OnAirLight, small: boolean) =>
  light === 'green' ? (small ? 'on air · klein' : 'on air') : light === 'yellow' ? 'bereit machen' : 'nicht live';

function PreviewTile({
  source,
  slot,
  stream,
  look,
  peerState,
  isSelf,
  onAir,
  isOverlay,
  split,
  light,
  talk,
  talkBlock,
  onTalk,
  send,
  onTake,
  onPip,
  onCue,
  onDrop,
}: {
  source: StreamSource;
  /** Feste Nummer der Kachel -- 1 bis 9 sind zugleich die Tastenkuerzel. */
  slot: number | undefined;
  stream?: MediaStream;
  look: CameraLook;
  peerState: PeerState;
  isSelf: boolean;
  onAir: boolean;
  isOverlay: boolean;
  /** Die zweite Kamera steht im Splitscreen -- dann heisst der Knopf „Daneben" statt „Klein". */
  split: boolean;
  light: OnAirLight;
  /**
   * Gegensprechen mit diesem Handy: `live` im Bild (Leitung immer offen),
   * `key` per Sprechtaste, `off` gar nicht. `null`: keine Taste -- die
   * eigene Kamera und andere Regie-Geraete.
   */
  talk: 'live' | 'key' | 'off' | null;
  /**
   * Warum die Regie gerade nicht zu hoeren ist: keine Kamera (`nomic`) oder
   * „Mikro aus" an der eigenen Kamera (`muted`). Ein laufendes Vorgespraech
   * laesst sich trotzdem beenden.
   */
  talkBlock: 'nomic' | 'muted' | null;
  onTalk: () => void;
  send: (m: ClientMessage) => void;
  onTake: () => void;
  onPip: () => void;
  /** Ampel dieser Quelle auf Gelb oder zurueck. */
  onCue: () => void;
  onDrop: () => void;
}) {
  const ref = useVideoStream(stream);
  // Die Wiederverbindung ist die genauere Auskunft: sie kommt vom Server und
  // sagt, dass die Quelle gleich zurueck ist -- nicht nur, dass es hakt.
  const trouble = !source.reconnecting && (peerState === 'failed' || peerState === 'disconnected');
  /** Tooltip mit Kuerzel -- ab Nummer 10 gibt es keins. */
  const withKey = (text: string, id: 'take' | 'second' | 'talk') =>
    hasSlotKey(slot) ? `${text} (${keyLabel(id, slot)})` : text;
  return (
    <div className="preview" data-light={light}>
      <div className="preview__thumb">
        <video
          ref={ref}
          className={camOn(source.feed) ? undefined : 'is-blank'}
          autoPlay
          playsInline
          muted
          style={videoLook(look)}
        />
        {!stream && <div className="placeholder">{peerStateLabel[peerState]}</div>}
        {/* Abgeblendet sieht sonst aus wie „Bild kommt nicht" -- und genau das
            waere die falsche Diagnose. */}
        {stream && !camOn(source.feed) && (
          <div className="placeholder">{source.feed.cam ? 'Bild vom Sender aus' : 'Bild aus'}</div>
        )}
        {/* Ob Ton ankommt -- ein Handy mit Bild und ohne Ton faellt sonst erst
            auf, wenn jemand im Stream zuhoert. */}
        <LevelMeter
          stream={stream}
          off={!micOn(source.feed) && !(source.kind === 'screen' && source.feed.system)}
          className="level--thumb"
        />
        <OnAirLamp light={light} text={lampText(light, isOverlay)} size="sm" className="ampel--tag" />
        {source.reconnecting && <span className="preview__tag preview__tag--warn">verbindet neu …</span>}
        {trouble && <span className="preview__tag preview__tag--warn">{peerStateLabel[peerState]}</span>}
        {!isSelf && source.geo.status !== 'inside' && (
          <span className={`preview__tag preview__tag--geo geotag--${source.geo.status}`}>
            {/* Bei ungenauer Ortung waere eine Entfernung eine erfundene Zahl --
                dann sagen wir lieber, wie unsicher der Standort ist. */}
            {source.geo.status === 'outside' && source.geo.distanceMeters !== null
              ? `${formatDistance(source.geo.distanceMeters)} entfernt`
              : source.geo.status === 'imprecise' && source.geo.accuracy !== null
                ? `Standort ±${formatDistance(source.geo.accuracy)}`
                : 'kein Standort'}
          </span>
        )}
      </div>
      <div className="preview__body">
        <b>
          {slot !== undefined && (
            <span
              className="preview__num"
              title={
                hasSlotKey(slot)
                  ? `Quelle ${slot}: ${keyLabel('take', slot)} Hauptbild · ${keyLabel('second', slot)} ${split ? 'daneben' : 'klein'}`
                  : `Quelle ${slot}`
              }
            >
              {slot}
            </span>
          )}
          {source.kind === 'screen' ? '🖥 ' : ''}
          {source.label}
        </b>
        <span>
          {source.ingest
            ? 'RTMP-Eingang · Ton und Bild wie bei jedem Handy schaltbar'
            : `${isSelf ? 'du (Regie)' : source.displayName} · ${roleLabel[source.role]}${source.bib ? ` · Nr. ${source.bib}` : ''}`}
        </span>
        <div className="preview__audio">
          <FeedSwitches source={source} send={send} compact />
          {talk && (
            <button
              type="button"
              className={`feed__btn talk-btn${talk === 'off' || talkBlock === 'muted' ? '' : ' is-talking'}`}
              disabled={talk === 'live' || (talkBlock !== null && talk !== 'key')}
              aria-pressed={talk !== 'off'}
              onClick={onTalk}
              title={
                talkBlock === 'muted'
                  ? talk === 'key'
                    ? 'Dein Mikro ist aus — das Handy hört dich nicht. Klick beendet das Vorgespräch.'
                    : 'Dein Mikro ist aus — das Handy hört dich nicht. Schalte es an deiner Kamera wieder ein.'
                  : talk === 'live'
                    ? 'Im Bild ist die Leitung offen — ihr hört euch, und es geht in den Stream.'
                    : talkBlock === 'nomic'
                      ? 'Die Sprechtaste nutzt das Mikro deiner Kamera — erst die Kamera starten.'
                      : withKey(
                          talk === 'key' ? 'Vorgespräch beenden' : 'Mit diesem Handy sprechen, bevor es live geht — nicht im Stream',
                          'talk',
                        )
              }
            >
              <span aria-hidden>🎙</span>
              <span className="only-wide">
                {talkBlock === 'muted'
                  ? 'Mikro ist aus'
                  : talk === 'live'
                    ? 'Live-Gespräch'
                    : talk === 'key'
                      ? 'Vorgespräch'
                      : 'Sprechen'}
              </span>
            </button>
          )}
          {/* Die eigene Kamera braucht kein „Bereit", ein RTMP-Eingang hat
              niemanden, der es saehe. */}
          {!isSelf && !source.ingest && (
            <button
              type="button"
              className={`feed__btn cue-btn${light === 'yellow' ? ' is-cued' : ''}`}
              disabled={light === 'green'}
              aria-pressed={light === 'yellow'}
              onClick={onCue}
              title={
                light === 'green'
                  ? 'Im Bild — die Ampel steht auf Grün.'
                  : light === 'yellow'
                    ? 'Ampel zurück auf Rot'
                    : 'Ampel auf Gelb: „Mach dich bereit — gleich bist du im Stream"'
              }
            >
              <span aria-hidden>🚦</span>
              <span className="only-wide">{light === 'yellow' ? 'Bereit ✓' : 'Bereit'}</span>
            </button>
          )}
        </div>
        <div className="preview__actions">
          <button className="btn btn--sm" onClick={onTake} disabled={onAir} title={withKey('Ins Hauptbild', 'take')}>
            {onAir ? 'läuft' : 'Hauptbild'}
          </button>
          <button
            className="btn btn--sm btn--onstage"
            onClick={onPip}
            disabled={onAir || isOverlay}
            title={withKey(split ? 'Als zweite Kamera daneben' : 'Als kleines Fenster', 'second')}
          >
            {split ? (isOverlay ? 'daneben ✓' : 'Daneben') : isOverlay ? 'klein ✓' : 'Klein'}
          </button>
          {/* Ein RTMP-Eingang wird im Block „RTMP-Eingang" entfernt -- hier gibt es keinen Tab zum Trennen. */}
          {!isSelf && !source.ingest && (
            <button className="btn btn--sm btn--onstage" onClick={onDrop} title="Stream dieser Person beenden">
              Trennen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ Startliste */

/** `Anna Winter` → `AW` -- wenn es noch keinen Headshot gibt. */
const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/** Welche Netzwerke eingetragen sind -- hoechstens drei Zeichen, sonst bliebe vom Namen nichts. */
function RosterSocials({ participant }: { participant: Participant }) {
  const set = SOCIALS.filter((s) => participant.socials?.[s.id]);
  if (!set.length) return null;
  return (
    <span className="roster__socials" title={set.map((s) => s.label).join(', ')}>
      {set.slice(0, 3).map((s) => (
        <SocialIcon key={s.id} id={s.id} />
      ))}
      {set.length > 3 && <small>+{set.length - 3}</small>}
    </span>
  );
}

function Avatar({ slug, participant, src }: { slug: string; participant: Participant; src?: string | null }) {
  const url = src !== undefined ? src : headshotUrl(slug, participant.id, participant.photoUpdatedAt);
  return (
    <span className={`roster__avatar${url ? '' : ' is-empty'}`}>
      {url ? <img src={url} alt="" /> : initialsOf(participant.name)}
    </span>
  );
}

/**
 * Angaben einer Person fuers Athleten-Pop-up. Das Foto wird im Browser auf
 * 600 px verkleinert (`HEADSHOT_PRESET`) und geht erst mit „Speichern" hoch --
 * bis dahin steht es nur als Vorschau da. Netzwerke duerfen Name oder Link
 * sein; was im Bild steht, macht daraus `socialLabel`.
 */
function ParticipantEditor({
  slug,
  pin,
  participant,
  onSaved,
  onClose,
}: {
  slug: string;
  pin: string;
  participant: Participant;
  onSaved: (participants: Participant[]) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(participant.name);
  const [team, setTeam] = useState(participant.team ?? '');
  const [info, setInfo] = useState(participant.info ?? '');
  const [socials, setSocials] = useState<ParticipantSocials>(participant.socials ?? {});
  /** undefined: Foto bleibt, null: weg, Text: neues Foto als data:-URL. */
  const [photo, setPhoto] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preview = photo === undefined ? headshotUrl(slug, participant.id, participant.photoUpdatedAt) : photo;

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    try {
      setPhoto(await fileToDataUrl(file, HEADSHOT_PRESET));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Foto konnte nicht gelesen werden.');
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.updateParticipant(slug, pin, participant.id, {
        name: name.trim() || undefined,
        team,
        info,
        socials,
        ...(photo !== undefined ? { photo } : {}),
      });
      onSaved(r.participants);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="roster-edit" onSubmit={save}>
      <div className="roster-edit__photo">
        <Avatar slug={slug} participant={{ ...participant, name }} src={preview} />
        <label className="btn btn--sm btn--onstage">
          {preview ? 'Anderes Foto' : 'Headshot wählen'}
          <input type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0])} />
        </label>
        {preview && (
          <button type="button" className="btn btn--sm btn--onstage" onClick={() => setPhoto(null)}>
            Entfernen
          </button>
        )}
        <HelpLink topic="athleten-popup" />
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" aria-label="Name" maxLength={80} />
      <input
        value={team}
        onChange={(e) => setTeam(e.target.value)}
        placeholder="Team / Verein"
        aria-label="Team oder Verein"
        maxLength={MAX_PARTICIPANT_TEAM}
      />
      <input
        value={info}
        onChange={(e) => setInfo(e.target.value)}
        placeholder="Info, z. B. Vorjahressiegerin · 38 Loops"
        aria-label="Info zur Person"
        maxLength={MAX_PARTICIPANT_INFO}
      />
      {SOCIALS.map((s) => (
        <label key={s.id} className="roster-edit__social">
          <SocialIcon id={s.id} />
          <input
            value={socials[s.id] ?? ''}
            onChange={(e) => setSocials({ ...socials, [s.id]: e.target.value })}
            placeholder={`${s.label}: ${s.placeholder}`}
            aria-label={s.label}
            maxLength={MAX_SOCIAL_LENGTH}
          />
        </label>
      ))}
      <div className="row gap-8">
        <button className="btn btn--sm" type="submit" disabled={busy}>
          {busy ? 'Speichert …' : 'Speichern'}
        </button>
        <button className="btn btn--sm btn--onstage" type="button" onClick={onClose}>
          Abbrechen
        </button>
      </div>
      {error && <span className="stage-bar__sub" style={{ color: 'var(--red-500)' }}>{error}</span>}
    </form>
  );
}

/**
 * Startliste der Regie. Die Ueberschrift klappt sie auf und zu -- die Daten
 * bleiben dabei geladen, damit die Zahl in der Ueberschrift stimmt.
 *
 * Je Person: das Athleten-Pop-up ausloesen und -- mit ✎ -- die Angaben dafuer
 * pflegen (Headshot, Team, ein Satz, Netzwerke; `ParticipantEditor`).
 */
function RosterPanel({
  slug,
  pin,
  open,
  onToggle,
  spotBib,
  send,
}: {
  slug: string;
  pin: string;
  open: boolean;
  onToggle: () => void;
  /** Wessen Athleten-Pop-up gerade laeuft. */
  spotBib: string | null;
  send: (m: ClientMessage) => void;
}) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [editing, setEditing] = useState<string | null>(null);
  const [bib, setBib] = useState('');
  const [name, setName] = useState('');
  const [bulk, setBulk] = useState('');
  const [showBulk, setShowBulk] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .getEvent(slug)
      .then((r) => setParticipants(r.event.participants))
      .catch(() => undefined);
  }, [slug]);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!bib.trim() || !name.trim()) return;
    try {
      const r = await api.addParticipant(slug, pin, { bib: bib.trim(), name: name.trim() });
      setParticipants(r.participants);
      setBib('');
      setName('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehlgeschlagen');
    }
  };

  const importBulk = async () => {
    setError(null);
    try {
      const r = await api.bulkParticipants(slug, pin, bulk);
      setParticipants(r.participants);
      setBulk('');
      setShowBulk(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fehlgeschlagen');
    }
  };

  /*
   * Die ganze Zeile klappt, wie bisher -- nur das „?" darin nicht. Ein Knopf
   * im Knopf geht nicht, deshalb nimmt die Zeile den Klick fuer die Maus,
   * und der Titel bleibt der Knopf fuer die Tastatur.
   */
  const head = (
    <div className="director-col__head director-col__head--toggle" onClick={onToggle}>
      <button
        type="button"
        className="director-col__toggle"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        aria-expanded={open}
        title={open ? 'Startliste einklappen' : 'Startliste aufklappen'}
      >
        <Chevron dir={open ? 'down' : 'right'} />
        Startliste
      </button>
      <HelpLink topic="startliste" />
      <span className="director-col__count">{participants.length}</span>
    </div>
  );
  if (!open) return head;

  return (
    <>
      {head}
      <form className="panel-form" onSubmit={add}>
        <div className="two">
          <input value={bib} onChange={(e) => setBib(e.target.value)} placeholder="Nr." />
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" />
        </div>
        <div className="row gap-8">
          <button className="btn btn--sm" type="submit">
            Hinzufügen
          </button>
          <button className="btn btn--sm btn--onstage" type="button" onClick={() => setShowBulk((v) => !v)}>
            Liste einfügen
          </button>
        </div>
        {showBulk && (
          <>
            <textarea
              rows={5}
              value={bulk}
              onChange={(e) => setBulk(e.target.value)}
              placeholder={'101;Mira Schulte\n102;Jonas Feddersen'}
            />
            <button className="btn btn--sm" type="button" onClick={() => void importBulk()}>
              {bulk.split('\n').filter(Boolean).length} Zeilen importieren
            </button>
          </>
        )}
        {error && <span className="stage-bar__sub" style={{ color: 'var(--red-500)' }}>{error}</span>}
      </form>
      <ul className="roster">
        {participants.map((p) => (
          <li key={p.id} className={editing === p.id ? 'is-editing' : undefined}>
            <div className="roster__row">
              <span className="bib">{p.bib}</span>
              <Avatar slug={slug} participant={p} />
              <span className="roster__name">
                {p.name}
                {(p.team || p.socials) && (
                  <small>
                    {p.team && <span>{p.team}</span>}
                    <RosterSocials participant={p} />
                  </small>
                )}
              </span>
              <span className="roster__actions">
                <SpotlightButton bib={p.bib} activeBib={spotBib} send={send} />
                <button
                  type="button"
                  title="Foto, Team, Info und Netzwerke bearbeiten"
                  aria-expanded={editing === p.id}
                  onClick={() => setEditing((cur) => (cur === p.id ? null : p.id))}
                >
                  ✎
                </button>
                <button
                  type="button"
                  title="Entfernen"
                  onClick={() =>
                    void api
                      .removeParticipant(slug, pin, p.id)
                      .then((r) => setParticipants(r.participants))
                      .catch(() => undefined)
                  }
                >
                  ×
                </button>
              </span>
            </div>
            {editing === p.id && (
              <ParticipantEditor
                slug={slug}
                pin={pin}
                participant={p}
                onSaved={setParticipants}
                onClose={() => setEditing(null)}
              />
            )}
          </li>
        ))}
        {participants.length === 0 && <li className="empty">Startliste ist leer.</li>}
      </ul>
    </>
  );
}
